import Vue from 'vue';

/* A store for modal states - non persisting store */
const state = {
  assetModal: null,
  audioAssetModal: null,
  audioAssetRefModal: null,
  deleteAssetReferencesModal: null,
  globalVarsModal: false,
  exportModal: false,
  confirmationModal: null,
  boardPropertiesModal: null,
  elementPropertiesModal: null,
  componentPropertiesModal: null,
  browserUnsupportedModal: false,
  errorModal: null,
  commentPopover: null,
  projectImportErrorModal: false,
  prototypeAudioModal: null,
};

const getters = {
  /**
   * Returns whether the asset modal is opened
   */
  getAssetModal(state) {
    return state.assetModal;
  },

  /**
   * Returns whether the asset modal is opened
   */
  getAudioAssetModal(state) {
    return state.audioAssetModal;
  },

  /**
   * Returns whether the asset reference modal is opened
   */
  getAudioAssetRefModal(state) {
    return state.audioAssetRefModal;
  },

  /**
   * Returns whether the delete asset references modal is opened
   */
  getDeleteAssetReferencesModal(state) {
    return state.deleteAssetReferencesModal;
  },

  /**
   * Returns whether global vars modal is opened
   */
  getGlobalVarsModal(state) {
    return state.globalVarsModal;
  },

  /**
   * Return whether export modal is opened
   */
  getExportModal(state) {
    return state.exportModal;
  },

  /**
   * Return whether confirmation modal is opened
   */
  getConfirmationModal(state) {
    return state.confirmationModal;
  },

  /**
   * Returns which (if any) board properties modal is opened
   * @returns {?string} The board id
   */
  getBoardPropertiesModal(state) {
    return state.boardPropertiesModal;
  },

  /**
   * Returns which (if any) element properties modal is opened
   * @returns {?string} The element id
   */
  getElementPropertiesModal(state) {
    return state.elementPropertiesModal;
  },

  /**
   * Returns which (if any) component properties modal is opened
   * @returns {?string} The component id
   */
  getComponentPropertiesModal(state) {
    return state.componentPropertiesModal;
  },

  /**
   * Returns whether browser unsupported modal is opened
   */
  getBrowserUnsupportedModal(state) {
    return state.browserUnsupportedModal;
  },

  /**
   * Returns whether the error modal is opened
   * @returns {?Number} The status code of the request
   */
  getErrorModal(state) {
    return state.errorModal;
  },

  getCommentPopover(state) {
    return state.commentPopover;
  },

  getProjectImportErrorModal(state) {
    return state.projectImportErrorModal;
  },

  getPrototypeAudioModal(state) {
    return state.prototypeAudioModal;
  },

  isModalOpen(state) {
    const values = Object.values(state);
    for (let i = 0; i < values.length; i += 1) {
      if (values[i]) return true;
    }
    return false;
  },
};

const actions = {
  /**
   * Set the asset modal state
   * @param {Boolean} value
   */
  setAssetModal(context, { value }) {
    context.commit('setAssetModal', value);
  },

  /**
   * Set the audio asset modal state
   */
  setAudioAssetModal(context, { value }) {
    context.commit('setAudioAssetModal', value);
  },

  /**
   * Set the audio asset reference modal state
   */
  setAudioAssetRefModal(context, { value }) {
    context.commit('setAudioAssetRefModal', value);
  },

  /**
   * Set the deleete asset references modal state
   * @param {Boolean} value
   */
  setDeleteAssetReferencesModal(context, { value }) {
    context.commit('setDeleteAssetReferencesModal', value);
  },

  /**
   * Sets the global vars modal state
   */
  setGlobalVarsModal(context, { value }) {
    context.commit('setGlobalVarsModal', value);
  },

  /**
   * Sets the board properties modal state
   */
  setBoardPropertiesModal(context, { value }) {
    context.commit('setBoardPropertiesModal', value);
  },

  /**
   * Sets the element properties modal state
   */
  setElementPropertiesModal(context, { value }) {
    context.commit('setElementPropertiesModal', value);
  },

  /**
   * Sets the component properties modal state
   */
  setComponentPropertiesModal(context, { value }) {
    context.commit('setComponentPropertiesModal', value);
  },

  /**
   * Sets the export modal state
   */
  setExportModal(context, { value }) {
    context.commit('setExportModal', value);
  },

  /**
   * Sets the confirmation modal state
   */
  setConfirmationModal(context, { value }) {
    context.commit('setConfirmationModal', value);
  },

  /**
   * Sets the browser unsupported modal state
   */
  setBrowserUnsupportedModal(context, { value }) {
    context.commit('setBrowserUnsupportedModal', value);
  },

  /**
   * Sets the error modal state to a status code
   */
  setErrorModal(context, { value }) {
    context.commit('setErrorModal', value);
  },

  setProjectImportErrorModal(context, { value }) {
    context.commit('setProjectImportErrorModal', value);
  },

  setPrototypeAudioModal(context, { value }) {
    context.commit('setPrototypeAudioModal', value);
  },
};

const mutations = {
  /**
   * Sets the asset modal state
   */
  setAssetModal(state, value) {
    state.assetModal = value;
  },

  /**
   * Sets the audio asset modal state
   */
  setAudioAssetModal(state, value) {
    state.audioAssetModal = value;
  },

  /**
   * Sets the audio asset reference modal state
   */
  setAudioAssetRefModal(state, value) {
    state.audioAssetRefModal = value;
  },

  /**
   * Sets the asset modal state
   */
  setDeleteAssetReferencesModal(state, value) {
    state.deleteAssetReferencesModal = value;
  },

  /**
   * Sets the global vars modal state
   */
  setGlobalVarsModal(state, value) {
    state.globalVarsModal = value;
  },

  /**
   * Sets the export modal state
   */
  setExportModal(state, value) {
    state.exportModal = value;
  },

  /**
   * Sets the confirmation modal state
   */
  setConfirmationModal(state, value) {
    state.confirmationModal = value;
  },

  /**
   * Sets the board properties modal state
   */
  setBoardPropertiesModal(state, value) {
    state.boardPropertiesModal = value;
  },

  /**
   * Sets the element properties modal state
   */
  setElementPropertiesModal(state, value) {
    state.elementPropertiesModal = value;
  },

  /**
   * Sets the component properties modal state
   */
  setComponentPropertiesModal(state, value) {
    state.componentPropertiesModal = value;
  },

  /**
   * Sets the browser unsupported modal state
   */
  setBrowserUnsupportedModal(state, value) {
    state.browserUnsupportedModal = value;
  },

  /**
   * Sets the error modal to a status code
   */
  setErrorModal(state, value) {
    state.errorModal = value;
  },

  setCommentPopover(state, value) {
    state.commentPopover = value;
  },

  setProjectImportErrorModal(state, value) {
    state.projectImportErrorModal = value;
  },

  setPrototypeAudioModal(state, value) {
    state.prototypeAudioModal = value;
  },
};

const module = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default module;
